/**
 * Basket API Sync Action
 * Triggered by: arc:action:basketApiSync
 * Integrates with external shopping basket API for pricing and discount validation
 */

export const runBasketApiSync = async (data = {}) => {
    try {
        const {
            triggerType = 'cart_changed', // 'cart_changed', 'discount_submit', 'gift_card_submit'
            discountCode = null,
            giftCardCode = null,
            customerId = null,
            sections = [],
            sections_url = window.location.pathname
        } = data;

        // Get current cart data
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) {
            throw new Error('Failed to fetch current cart data');
        }
        const cart = await cartResponse.json();

        // Get customer info if available
        let customerInfo = null;
        if (window.customerData || customerId) {
            customerInfo = {
                id: customerId || window.customerData?.id,
                email: window.customerData?.email,
                phone: window.customerData?.phone
            };
        }

        // Prepare basket API request payload (updated to match new structure)
        const basketPayload = {
            cart: {
                token: cart.token,
                validate_only: 'true',
                customer: {
                    email: '',
                    custcode: ''
                },
                line_items: {
                    line_items: cart.items.map(item => ({
                        name: item.product_title || item.title || '',
                        sku: item.sku || item.variant_id?.toString() || '',
                        price: item.price / 100,
                        compare_price: item.compare_at_price ? item.compare_at_price / 100 : null,
                        quantity: item.quantity,
                        unique_id: item.sku || item.variant_id?.toString() || ''
                    }))
                },
                discounts_applied: { discounts_applied: [] },
                giftcards_applied: { giftcards_applied: [] }
            }
        };

        // Add new discount/gift card if submitting (optional, not in cart object)
        if (triggerType === 'discount_submit' && discountCode) {
            basketPayload.cart.discounts_applied.discounts_applied.push({ code: discountCode });
        }
        if (triggerType === 'gift_card_submit' && giftCardCode) {
            basketPayload.cart.giftcards_applied.giftcards_applied.push({ code: giftCardCode });
        }

        // Call basket API endpoint
        let basketApiUrl = '';
        if (window.basketApiConfig && window.basketApiConfig.endpoint) {
            basketApiUrl = window.basketApiConfig.endpoint;
        } else {
            basketApiUrl = `https://${window.Shopify && window.Shopify.shop ? window.Shopify.shop : window.location.hostname}/apps/strandbags/api/2021-04/basket/check`;
        }

        console.log('Basket API URL:', basketApiUrl);
        const basketResponse = await fetch(basketApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(window.basketApiConfig?.headers || {})
            },
            body: JSON.stringify(basketPayload)
        });

        if (!basketResponse.ok) {
            const errorData = await basketResponse.json().catch(() => ({}));
            throw new Error(errorData.message || `Basket API request failed: ${basketResponse.status}`);
        }

        const basketResult = await basketResponse.json();

        // Process the response
        let cartUpdateNeeded = false;
        const updateActions = [];

        // Handle price and discount updates from new response structure
        if (
            basketResult &&
            basketResult.cart &&
            basketResult.cart.cart &&
            basketResult.cart.cart.line_items &&
            basketResult.cart.cart.line_items.line_items
        ) {
            const responseLineItems = basketResult.cart.cart.line_items.line_items;
            for (const lineItem of responseLineItems) {
                // Improved matching: try variant_id, sku, unique_id
                const shopifyItem = cart.items.find(i => {
                    const variantIdMatch = i.variant_id?.toString() === lineItem.unique_id?.toString() || i.variant_id?.toString() === lineItem.sku?.toString();
                    const skuMatch = i.sku && (i.sku.toString() === lineItem.sku?.toString() || i.sku.toString() === lineItem.unique_id?.toString());
                    return variantIdMatch || skuMatch;
                });
                if (shopifyItem) {
                    // Convert new_price to cents (string)
                    let newPriceCents = '';
                    if (lineItem.new_price) {
                        newPriceCents = Math.round(parseFloat(lineItem.new_price) * 100).toString();
                    }
                    // Extract discount_value if present
                    let discountValue = '';
                    if (lineItem.discounts_applied && lineItem.discounts_applied.discount_codes && lineItem.discounts_applied.discount_codes.length > 0) {
                        discountValue = lineItem.discounts_applied.discount_codes[0].discount_value || '';
                    }
                    // Debug output
                    console.log('Updating line item', {
                        shopifyItem,
                        lineItem,
                        newPriceCents,
                        discountValue
                    });
                    updateActions.push({
                        type: 'update_line_item_properties',
                        properties: {
                            ...shopifyItem.properties,
                            ...(newPriceCents && { _new_price: newPriceCents }),
                            ...(lineItem.message && { _discount_message: lineItem.message }),
                            ...(lineItem.discount_applied_number && { _discount_applied_number: lineItem.discount_applied_number }),
                            ...(discountValue && { _discount_value: discountValue })
                        }
                    });
                    cartUpdateNeeded = true;
                } else {
                    // Debug output for unmatched items
                    console.warn('No matching Shopify cart item for API line item', lineItem);
                }
            }
        }

        // Handle successful discount applications
        if (basketResult.discount_applications) {
            for (const discount of basketResult.discount_applications) {
                if (discount.success && discount.code) {
                    // Discount was successfully applied by basket API
                    console.log(`Discount ${discount.code} applied successfully`);
                }
            }
        }

        // Handle successful gift card applications
        if (basketResult.gift_card_applications) {
            for (const giftCard of basketResult.gift_card_applications) {
                if (giftCard.success && giftCard.code) {
                    // Gift card was successfully applied by basket API
                    console.log(`Gift card ${giftCard.code} applied successfully`);
                }
            }
        }

        // Apply cart updates if needed
        if (cartUpdateNeeded) {
            await applyBasketApiUpdates(updateActions);
        }

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('basket_api:sync_complete', {
            detail: {
                triggerType,
                basketResult,
                cartUpdateNeeded,
                discountApplications: basketResult.discount_applications || [],
                giftCardApplications: basketResult.gift_card_applications || []
            }
        }));

        return {
            success: true,
            basketResult,
            cartUpdateNeeded,
            message: 'Basket API sync completed successfully'
        };

    } catch (error) {
        console.error('Error syncing with basket API:', error);

        // Dispatch error event
        window.dispatchEvent(new CustomEvent('basket_api:sync_error', {
            detail: {
                error: error.message,
                triggerType: data.triggerType
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Apply updates from basket API to the cart
 */
const applyBasketApiUpdates = async (updateActions) => {
    for (const action of updateActions) {
        if (action.type === 'update_line_item_properties') {
            try {
                // Update line item properties and preserve quantity
                const updateResponse = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: action.key,
                        quantity: action.quantity,
                        properties: action.properties
                    })
                });

                if (!updateResponse.ok) {
                    console.warn(`Failed to update properties for variant ${action.variant_id}`);
                }
            } catch (error) {
                console.warn(`Error updating variant ${action.variant_id}:`, error);
            }
        }
    }

    // Refresh cart after all updates
    try {
        const refreshResponse = await fetch('/cart.js');
        if (refreshResponse.ok) {
            const updatedCart = await refreshResponse.json();
            window.dispatchEvent(new CustomEvent('cart:updated', {
                detail: {
                    cart: updatedCart,
                    action: 'basket_api_update'
                }
            }));
        }
    } catch (error) {
        console.warn('Failed to refresh cart after basket API updates:', error);
    }
};

export default runBasketApiSync;
